import { useEffect, useRef, useState } from 'react';
import { Application, Graphics, Text, Container, Sprite, Texture } from 'pixi.js';
import backgroundImage from './background_graphic.png';

interface DataPoint {
  index: number;
  x: string;
  y: number;
  z?: number;
}

interface AnimatedGraphProps {
  data: DataPoint[];
  width: number;
  height: number;
  showPoints: boolean;
  animationSpeed: number;
  backgroundColor: string;
  axisLabels: { x: string; y: string; z?: string } | null;
  minYLabel: string;
  maxYLabel: string;
  minZLabel: string;
  maxZLabel: string;
  labelFontSize: number;
  showMinYLabel: boolean;
  showMaxYLabel: boolean;
  showMinZLabel: boolean;
  showMaxZLabel: boolean;
  pauseOnMinMax: boolean;
  pauseDuration: number;
  onRecordingComplete?: (blob: Blob) => void;
  captureVideo: boolean;
  showWatermark: boolean;
}

export function AnimatedGraph({ 
  data, 
  width, 
  height, 
  showPoints, 
  animationSpeed, 
  backgroundColor, 
  axisLabels, 
  minYLabel, 
  maxYLabel, 
  minZLabel, 
  maxZLabel, 
  labelFontSize,
  showMinYLabel,
  showMaxYLabel,
  showMinZLabel,
  showMaxZLabel,
  pauseOnMinMax = false,
  pauseDuration = 1000,
  onRecordingComplete,
  captureVideo,
  showWatermark
}: AnimatedGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const animationRef = useRef<number | null>(null);
  const [stats, setStats] = useState<{ minX: number; maxX: number; minY: number; maxY: number } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    if (!appRef.current?.canvas) {
      console.log('Cannot start recording: Canvas not available');
      return;
    }

    // Check for supported MIME types in order of preference for Twitter/X compatibility
    const mimeTypes = [
      'video/mp4;codecs=avc1.42E01E', // H.264 Baseline Profile
      'video/mp4;codecs=avc1.4D401E', // H.264 Main Profile
      'video/mp4;codecs=avc1.64001E', // H.264 High Profile
      'video/mp4;codecs=h264',        // Generic H.264
      'video/mp4',                    // Generic MP4
      'video/webm;codecs=h264',       // WebM with H.264 (rare but possible)
      'video/webm;codecs=vp9',        // VP9 as fallback
      'video/webm;codecs=vp8',        // VP8 as fallback
      'video/webm'                    // Generic WebM
    ];

    console.log('Checking supported MIME types for Twitter/X compatibility:');
    mimeTypes.forEach(mimeType => {
      const isSupported = MediaRecorder.isTypeSupported(mimeType);
      console.log(`${mimeType}: ${isSupported ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    });

    let selectedMimeType = null;
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        console.log('Selected MIME type for recording:', mimeType);
        break;
      }
    }

    if (!selectedMimeType) {
      console.error('No supported video format found');
      return;
    }

    try {
      const stream = appRef.current.canvas.captureStream(30); // 30 FPS
      
      // Try to configure the MediaRecorder with specific codec settings
      const options: MediaRecorderOptions = {
        mimeType: selectedMimeType
      };

      // Add additional options for better H.264 compatibility
      if (selectedMimeType.includes('mp4') || selectedMimeType.includes('h264') || selectedMimeType.includes('avc1')) {
        console.log('Using MP4/H.264 configuration for Twitter/X compatibility');
      }

      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Received video chunk:', event.data.size, 'bytes, type:', event.data.type);
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log('Recording stopped. Processing video...');
        
        // Check the actual type of the first chunk to determine the real format
        let actualType = 'video/mp4';
        if (recordedChunksRef.current.length > 0) {
          const firstChunk = recordedChunksRef.current[0];
          actualType = firstChunk.type || selectedMimeType;
          console.log('First chunk type:', firstChunk.type, 'Selected MIME type:', selectedMimeType);
        }
        
        // Determine the correct file extension and blob type
        let fileExtension = 'mp4';
        let blobType = 'video/mp4';
        
        // Force MP4 container for better Twitter/X compatibility
        if (selectedMimeType.includes('mp4') || selectedMimeType.includes('h264') || selectedMimeType.includes('avc1')) {
          fileExtension = 'mp4';
          blobType = 'video/mp4';
          console.log('Using MP4 container for Twitter/X compatibility');
        } else if (actualType.includes('webm') || selectedMimeType.includes('webm')) {
          fileExtension = 'webm';
          blobType = 'video/webm';
          console.log('Falling back to WebM container');
        }
        
        const blob = new Blob(recordedChunksRef.current, {
          type: blobType
        });
        console.log('Video blob created:', blob.size, 'bytes, type:', blobType, 'actual chunks type:', actualType);
        console.log('File will be saved as:', `animated-line-graph.${fileExtension}`);
        recordedChunksRef.current = [];
        if (onRecordingComplete) {
          console.log('Calling onRecordingComplete callback');
          onRecordingComplete(blob);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      console.log('MediaRecorder started with MIME type:', selectedMimeType);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    } else {
      console.log('No active recording to stop');
    }
  };

  // Start recording when animation starts if captureVideo is true
  useEffect(() => {
    if (captureVideo && isAnimating && !isRecording) {
      console.log('Animation started with captureVideo enabled, starting recording');
      startRecording();
    }
  }, [isAnimating, captureVideo]);

  // Stop recording when animation ends
  useEffect(() => {
    if (!isAnimating && isRecording) {
      console.log('Animation ended, stopping recording');
      stopRecording();
    }
  }, [isAnimating]);

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;

    console.log('Container dimensions:', { width, height });

    // Create the application if it doesn't exist
    if (!appRef.current) {
      const app = new Application();
      
      // Initialize the application with the new method
      app.init({
        width,
        height,
        backgroundColor: backgroundColor === 'black' ? 0x000000 : 
                        backgroundColor === '#f0f0f0' ? 0xf0f0f0 :
                        backgroundColor === '#404040' ? 0x404040 : 0xffffff,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      }).then(() => {
        if (containerRef.current && app.canvas) {
          app.canvas.style.width = `${width}px`;
          app.canvas.style.height = `${height}px`;
          app.canvas.style.display = 'block';
          containerRef.current.appendChild(app.canvas);
          appRef.current = app;
          console.log('PixiJS Application initialized');
        }
      });
    }

    const app = appRef.current;
    if (!app) return;

    // Update background color if it changed
    app.renderer.background.color = backgroundColor === 'black' ? 0x000000 : 
                                  backgroundColor === '#f0f0f0' ? 0xf0f0f0 :
                                  backgroundColor === '#404040' ? 0x404040 : 0xffffff;

    // Clear previous content and animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    app.stage.removeChildren();
    setIsAnimating(true);

    // Add watermark text if enabled and user hasn't paid
    if (showWatermark && localStorage.getItem('watermarkRemoved') !== 'true') {
      // Determine watermark color based on background
      const isDarkBackground = backgroundColor === 'black' || backgroundColor === '#404040';
      const watermarkColor = isDarkBackground ? 0xffffff : 0x000000;
      
      const watermark = new Text({
        text: 'graffy.ca',
        style: {
          fontSize: 48,
          fill: watermarkColor,
          fontWeight: 'bold'
        }
      });
      // Make it very subtle
      watermark.alpha = 0.1;
      // Center the watermark
      watermark.x = (width - watermark.width) / 2;
      watermark.y = (height - watermark.height) / 2;
      app.stage.addChild(watermark);
    }

    // Calculate scales using index for x-axis
    const xValues = data.map(point => point.index);
    const yValues = data.map(point => point.y);
    const zValues = data.map(point => point.z).filter((z): z is number => z !== undefined);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const minZ = zValues.length > 0 ? Math.min(...zValues) : minY;
    const maxZ = zValues.length > 0 ? Math.max(...zValues) : maxY;

    // Update stats
    setStats({ minX, maxX, minY, maxY });

    // Add some padding to the scales
    const xPadding = (maxX - minX) * 0.1;
    const yPadding = (maxY - minY) * 0.1;
    const zPadding = (maxZ - minZ) * 0.1;
    const paddedMinX = Math.max(0, minX - xPadding);
    const paddedMaxX = maxX + xPadding;
    const paddedMinY = minY - yPadding;
    const paddedMaxY = maxY + yPadding;
    const paddedMinZ = minZ - zPadding;
    const paddedMaxZ = maxZ + zPadding;

    // Calculate margins for the graph
    const margin = 80;
    const rightMargin = zValues.length > 0 ? 100 : margin; // Extra margin for Z axis
    const graphWidth = width - (margin + rightMargin);
    const graphHeight = height - (margin * 2);

    const xScale = graphWidth / (paddedMaxX - paddedMinX || 1);
    const yScale = graphHeight / (paddedMaxY - paddedMinY || 1);
    const zScale = graphHeight / (paddedMaxZ - paddedMinZ || 1);

    console.log('Scales:', { xScale, yScale, minX, maxX, minY, maxY });

    // Determine text color based on background
    const isDarkBackground = backgroundColor === 'black' || backgroundColor === '#404040';
    const textColor = isDarkBackground ? 0xffffff : 0x000000;
    const gridColor = isDarkBackground ? 0x444444 : 0xdddddd;

    // Draw grid lines
    const grid = new Graphics();
    grid.setStrokeStyle({ width: 1, color: gridColor });
    
    // Draw horizontal grid lines for Y axis
    const yStep = (paddedMaxY - paddedMinY) / 5;
    for (let i = 0; i <= 5; i++) {
      const y = height - margin - (i * yStep * yScale);
      grid.moveTo(margin, y)
        .lineTo(width - rightMargin, y);
      
      // Add y-axis labels
      const label = new Text({
        text: (paddedMinY + i * yStep).toFixed(1),
        style: {
          fontSize: 12,
          fill: textColor,
        }
      });
      label.x = margin - 40;
      label.y = y - 6;
      app.stage.addChild(label);
    }

    // Draw horizontal grid lines for Z axis if it exists
    if (zValues.length > 0) {
      const zStep = (paddedMaxZ - paddedMinZ) / 5;
      for (let i = 0; i <= 5; i++) {
        const y = height - margin - (i * zStep * zScale);
        grid.moveTo(width - rightMargin, y)
          .lineTo(width - margin, y);
        
        // Add z-axis labels
        const label = new Text({
          text: (paddedMinZ + i * zStep).toFixed(1),
          style: {
            fontSize: 12,
            fill: textColor,
          }
        });
        label.x = width - rightMargin + 10;
        label.y = y - 6;
        app.stage.addChild(label);
      }
    }

    // Draw vertical grid lines and labels
    const xStep = (paddedMaxX - paddedMinX) / 5;
    for (let i = 0; i <= 5; i++) {
      const x = margin + (i * xStep * xScale);
      grid.moveTo(x, margin)
        .lineTo(x, height - margin);
      
      // Skip the last label if it would be based on count
      if (i === 5) continue;
      
      // Add x-axis labels using the actual x string values
      const index = Math.round(paddedMinX + i * xStep);
      const point = data.find(p => p.index === index);
      const labelText = point ? point.x : index.toString();
      
      const label = new Text({
        text: labelText,
        style: {
          fontSize: 12,
          fill: textColor,
        }
      });
      label.x = x - 10;
      label.y = height - margin + 10;
      app.stage.addChild(label);
    }
    
    // Make sure to stroke the grid lines
    grid.stroke();
    app.stage.addChild(grid);

    // Draw x-axis
    const xAxis = new Graphics();
    xAxis.setStrokeStyle({ width: 2, color: textColor })
      .moveTo(margin, height - margin)
      .lineTo(width - rightMargin, height - margin)
      .stroke();
    app.stage.addChild(xAxis);

    // Draw y-axis
    const yAxis = new Graphics();
    yAxis.setStrokeStyle({ width: 2, color: textColor })
      .moveTo(margin, margin)
      .lineTo(margin, height - margin)
      .stroke();
    app.stage.addChild(yAxis);

    // Draw z-axis if it exists
    if (zValues.length > 0) {
      const zAxis = new Graphics();
      zAxis.setStrokeStyle({ width: 2, color: textColor })
        .moveTo(width - rightMargin, margin)
        .lineTo(width - rightMargin, height - margin)
        .stroke();
      app.stage.addChild(zAxis);
    }

    // Add axis labels
    const xLabel = new Text({
      text: axisLabels?.x || 'X',
      style: {
        fontSize: 16,
        fill: textColor,
      }
    });
    xLabel.x = width - rightMargin - xLabel.width + 50;
    xLabel.y = height - margin + 10;
    app.stage.addChild(xLabel);

    // Add Y axis label with blue underline
    const yLabel = new Text({
      text: axisLabels?.y || 'Y',
      style: {
        fontSize: 16,
        fill: textColor,
      }
    });
    yLabel.x = margin - 20;
    yLabel.y = margin - 40;
    app.stage.addChild(yLabel);

    // Add blue underline for Y axis label
    const yUnderline = new Graphics();
    yUnderline.setStrokeStyle({ width: 2, color: 0x0000ff })
      .moveTo(margin - 20, margin - 20)
      .lineTo(margin - 20 + yLabel.width, margin - 20)
      .stroke();
    app.stage.addChild(yUnderline);

    if (zValues.length > 0) {
      const zLabel = new Text({
        text: axisLabels?.z || 'Z',
        style: {
          fontSize: 16,
          fill: textColor,
        }
      });
      zLabel.x = width - rightMargin + 10;
      zLabel.y = margin - 40;
      app.stage.addChild(zLabel);

      // Add red underline for Z axis label
      const zUnderline = new Graphics();
      zUnderline.setStrokeStyle({ width: 2, color: 0xff0000 })
        .moveTo(width - rightMargin + 10, margin - 20)
        .lineTo(width - rightMargin + 10 + zLabel.width, margin - 20)
        .stroke();
      app.stage.addChild(zUnderline);
    }

    // Create graphics objects for points and lines
    const pointsGraphics = new Graphics();
    const linesGraphics = new Graphics();
    app.stage.addChild(linesGraphics);
    app.stage.addChild(pointsGraphics);

    // Animation variables
    let currentIndex = 0;
    let progress = 0;
    let minYPoint: { x: number; y: number; value: number } | null = null;
    let maxYPoint: { x: number; y: number; value: number } | null = null;
    let minZPoint: { x: number; y: number; value: number } | null = null;
    let maxZPoint: { x: number; y: number; value: number } | null = null;
    const drawnLabels = new Set<string>(); // Track which labels have been drawn
    const existingLabels: { x: number; y: number; width: number; height: number }[] = []; // Track label positions
    let isPaused = false; // Track if animation is paused
    let pauseStartTime = 0; // Track when pause started
    const PAUSE_DURATION = pauseDuration; // Use the prop value for pause duration
    let pendingLabels: { type: 'minY' | 'maxY' | 'minZ' | 'maxZ'; x: number; y: number; value: number }[] = []; // Track labels to be drawn

    // Helper function to check if two labels would overlap
    const wouldOverlap = (label1: { x: number; y: number; width: number; height: number }, 
                         label2: { x: number; y: number; width: number; height: number }) => {
      const padding = 5; // Add some padding between labels
      return !(label1.x + label1.width + padding < label2.x ||
               label2.x + label2.width + padding < label1.x ||
               label1.y + label1.height + padding < label2.y ||
               label2.y + label2.height + padding < label1.y);
    };

    // Helper function to adjust label position to avoid overlap
    const adjustLabelPosition = (label: { x: number; y: number; width: number; height: number },
                               existingLabels: { x: number; y: number; width: number; height: number }[]) => {
      const padding = 5;
      let newY = label.y;
      let attempts = 0;
      const maxAttempts = 10; // Limit the number of adjustment attempts

      while (attempts < maxAttempts) {
        let hasOverlap = false;
        for (const existingLabel of existingLabels) {
          if (wouldOverlap({ ...label, y: newY }, existingLabel)) {
            hasOverlap = true;
            break;
          }
        }
        if (!hasOverlap) break;
        newY -= 20; // Move label up by 20 pixels
        attempts++;
      }
      return newY;
    };

    // Find min and max points
    data.forEach((point) => {
      if (minYPoint === null || point.y < minYPoint.value) {
        minYPoint = { x: point.index, y: point.y, value: point.y };
      }
      if (maxYPoint === null || point.y > maxYPoint.value) {
        maxYPoint = { x: point.index, y: point.y, value: point.y };
      }
      if (point.z !== undefined) {
        if (minZPoint === null || point.z < minZPoint.value) {
          minZPoint = { x: point.index, y: point.z, value: point.z };
        }
        if (maxZPoint === null || point.z > maxZPoint.value) {
          maxZPoint = { x: point.index, y: point.z, value: point.z };
        }
      }
    });

    // Animation function
    const animate = () => {
      if (currentIndex >= data.length) {
        setIsAnimating(false);
        return;
      }

      // Handle pause state
      if (isPaused) {
        const currentTime = performance.now();
        if (currentTime - pauseStartTime < PAUSE_DURATION) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        isPaused = false;
      }

      const point = data[currentIndex];
      const x = margin + (point.index - paddedMinX) * xScale;
      const y = height - margin - (point.y - paddedMinY) * yScale;
      const z = point.z !== undefined ? height - margin - (point.z - paddedMinZ) * zScale : undefined;

      // Draw current point only if showPoints is true
      if (showPoints) {
        // Draw Y point
        pointsGraphics
          .circle(x, y, 6)
          .stroke({ width: 1, color: 0x000000 });
        pointsGraphics
          .fill({ color: 0x0000ff });

        // Draw Z point if it exists
        if (z !== undefined) {
          pointsGraphics
            .circle(x, z, 6)
            .stroke({ width: 1, color: 0x000000 });
          pointsGraphics
            .fill({ color: 0xff0000 });
        }
      }

      // Draw lines to previous points
      if (currentIndex > 0) {
        const prevPoint = data[currentIndex - 1];
        const prevX = margin + (prevPoint.index - paddedMinX) * xScale;
        const prevY = height - margin - (prevPoint.y - paddedMinY) * yScale;
        const prevZ = prevPoint.z !== undefined ? height - margin - (prevPoint.z - paddedMinZ) * zScale : undefined;

        // Clear previous line segment
        linesGraphics.clear();
        
        // Draw all completed lines first
        for (let i = 0; i < currentIndex; i++) {
          const p1 = data[i];
          const p2 = data[i + 1];
          const x1 = margin + (p1.index - paddedMinX) * xScale;
          const y1 = height - margin - (p1.y - paddedMinY) * yScale;
          const z1 = p1.z !== undefined ? height - margin - (p1.z - paddedMinZ) * zScale : undefined;
          const x2 = margin + (p2.index - paddedMinX) * xScale;
          const y2 = height - margin - (p2.y - paddedMinY) * yScale;
          const z2 = p2.z !== undefined ? height - margin - (p2.z - paddedMinZ) * zScale : undefined;
          
          // Draw Y line
          linesGraphics
            .setStrokeStyle({ width: 2, color: 0x0000ff })
            .moveTo(x1, y1)
            .lineTo(x2, y2)
            .stroke();

          // Draw Z line if it exists
          if (z1 !== undefined && z2 !== undefined) {
            linesGraphics
              .setStrokeStyle({ width: 2, color: 0xff0000 })
              .moveTo(x1, z1)
              .lineTo(x2, z2)
              .stroke();
          }
        }

        // Draw the current animating lines
        const currentX = prevX + (x - prevX) * progress;
        const currentY = prevY + (y - prevY) * progress;
        const currentZ = prevZ !== undefined && z !== undefined ? prevZ + (z - prevZ) * progress : undefined;

        // Draw Y line
        linesGraphics
          .setStrokeStyle({ width: 2, color: 0x0000ff })
          .moveTo(prevX, prevY)
          .lineTo(currentX, currentY)
          .stroke();

        // Draw Z line if it exists
        if (currentZ !== undefined) {
          linesGraphics
            .setStrokeStyle({ width: 2, color: 0xff0000 })
            .moveTo(prevX, prevZ!)
            .lineTo(currentX, currentZ)
            .stroke();
        }

        progress += animationSpeed;
        if (progress >= 1) {
          progress = 0;
          currentIndex++;

          // Check for min/max points after the line is drawn
          if (point.y === minYPoint?.value && showMinYLabel && !drawnLabels.has('minY')) {
            pendingLabels.push({ type: 'minY', x, y, value: point.y });
          }
          if (point.y === maxYPoint?.value && showMaxYLabel && !drawnLabels.has('maxY')) {
            pendingLabels.push({ type: 'maxY', x, y, value: point.y });
          }
          if (point.z !== undefined) {
            if (point.z === minZPoint?.value && showMinZLabel && !drawnLabels.has('minZ')) {
              pendingLabels.push({ type: 'minZ', x, y: z!, value: point.z });
            }
            if (point.z === maxZPoint?.value && showMaxZLabel && !drawnLabels.has('maxZ')) {
              pendingLabels.push({ type: 'maxZ', x, y: z!, value: point.z });
            }
          }
        }
      } else {
        currentIndex++;
      }

      // Draw any pending labels
      if (pendingLabels.length > 0) {
        const label = pendingLabels[0];
        let markerGraphics: Graphics;
        let labelText: Text;
        let color: number;
        let labelId: string;

        switch (label.type) {
          case 'minY':
            color = 0x800080; // Purple
            labelId = 'minY';
            markerGraphics = new Graphics()
              .setStrokeStyle({ width: 2, color })
              .moveTo(label.x, label.y - 20)
              .lineTo(label.x, label.y - 5)
              .moveTo(label.x - 5, label.y - 10)
              .lineTo(label.x, label.y - 5)
              .lineTo(label.x + 5, label.y - 10)
              .stroke();
            labelText = new Text({
              text: `${minYLabel}: ${label.value}`,
              style: {
                fontSize: labelFontSize,
                fill: color, // Purple
                fontWeight: 'bold'
              }
            });
            break;
          case 'maxY':
            color = 0x800080; // Purple
            labelId = 'maxY';
            markerGraphics = new Graphics()
              .setStrokeStyle({ width: 2, color })
              .moveTo(label.x, label.y + 20)
              .lineTo(label.x, label.y + 5)
              .moveTo(label.x - 5, label.y + 10)
              .lineTo(label.x, label.y + 5)
              .lineTo(label.x + 5, label.y + 10)
              .stroke();
            labelText = new Text({
              text: `${maxYLabel}: ${label.value}`,
              style: {
                fontSize: labelFontSize,
                fill: color, // Purple
                fontWeight: 'bold'
              }
            });
            break;
          case 'minZ':
            color = 0x800080; // Purple
            labelId = 'minZ';
            markerGraphics = new Graphics()
              .setStrokeStyle({ width: 2, color })
              .moveTo(label.x, label.y - 20)
              .lineTo(label.x, label.y - 5)
              .moveTo(label.x - 5, label.y - 10)
              .lineTo(label.x, label.y - 5)
              .lineTo(label.x + 5, label.y - 10)
              .stroke();
            labelText = new Text({
              text: `${minZLabel}: ${label.value}`,
              style: {
                fontSize: labelFontSize,
                fill: color, // Purple
                fontWeight: 'bold'
              }
            });
            break;
          case 'maxZ':
            color = 0x800080; // Purple
            labelId = 'maxZ';
            markerGraphics = new Graphics()
              .setStrokeStyle({ width: 2, color })
              .moveTo(label.x, label.y + 20)
              .lineTo(label.x, label.y + 5)
              .moveTo(label.x - 5, label.y + 10)
              .lineTo(label.x, label.y + 5)
              .lineTo(label.x + 5, label.y + 10)
              .stroke();
            labelText = new Text({
              text: `${maxZLabel}: ${label.value}`,
              style: {
                fontSize: labelFontSize,
                fill: color, // Purple
                fontWeight: 'bold'
              }
            });
            break;
        }

        labelText.x = label.x + 10;
        labelText.y = adjustLabelPosition(
          { x: label.x + 10, y: label.y + (label.type === 'minY' || label.type === 'minZ' ? -30 : 10), width: labelText.width, height: labelText.height },
          existingLabels
        );
        existingLabels.push({ x: labelText.x, y: labelText.y, width: labelText.width, height: labelText.height });
        app.stage.addChild(markerGraphics);
        app.stage.addChild(labelText);
        drawnLabels.add(labelId);
        pendingLabels.shift();

        if (pauseOnMinMax) {
          isPaused = true;
          pauseStartTime = performance.now();
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animate();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, [data, width, height, showPoints, animationSpeed, backgroundColor, axisLabels, minYLabel, maxYLabel, minZLabel, maxZLabel, labelFontSize, showMinYLabel, showMaxYLabel, showMinZLabel, showMaxZLabel, pauseOnMinMax, pauseDuration, showWatermark]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {stats && (
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <div>Min X: {stats.minX}</div>
          <div>Max X: {stats.maxX}</div>
          <div>Min Y: {stats.minY}</div>
          <div>Max Y: {stats.maxY}</div>
        </div>
      )}
      <div 
        ref={containerRef} 
        style={{ 
          width, 
          height,
          border: '2px solid #000000',
          backgroundColor: '#ffffff',
          position: 'relative'
        }} 
      />
      <button
        onClick={isRecording ? stopRecording : startRecording}
        style={{
          padding: '8px 16px',
          backgroundColor: isRecording ? '#dc3545' : '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'none' // Hide the button but keep it in the DOM
        }}
        id="recordingButton"
      >
        {isRecording ? 'Stop Recording' : 'Start Recording'}
      </button>
    </div>
  );
} 